import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../hooks/useAuth";
import { useTeenMode } from "../hooks/useTeenMode";
import { homeAPI } from "../services/api";
import { PageWrapper, Card, Button, Logo, StreakMilestoneModal } from "../components";
import { NotificationBell } from "../components/NotificationBell";
import { AtelierNavBadge } from "../components/AtelierNavBadge";
import TodaysActivationPill from "../components/TodaysActivationPill";
import WelcomeAudioCard from "../components/WelcomeAudioCard";
import AlignmentCheck from "../components/AlignmentCheck";
import { IntegrationCard } from "../components/IntegrationCard";
import WindowPicker from "../components/WindowPicker";
import ReclaimGroupTiles from "../components/ReclaimGroupTiles";
import TeamAlignmentBanner from "../components/TeamAlignment/TeamAlignmentBanner";
import SpinalMilestoneBloom from "./HealingSupport/SpinalMilestoneBloom";
import TeamAlignmentHomeCard from "../components/TeamAlignment/TeamAlignmentHomeCard";
import CarriedLineHero from "../components/TeamAlignment/CarriedLineHero";
import PartnerPackBanner from "../components/PartnerPackBanner";
import { Sparkles, Clock, ChevronRight, Trophy, Play, CheckCircle2, Flame, Leaf, Lock, Heart, Compass, Moon, Sun, Waves, Target, Users, BookmarkCheck, Globe, Settings } from "lucide-react";

// Time-of-day environment configuration - creates a living, breathing experience
const getTimeEnvironment = () => {
  const hour = new Date().getHours();
  
  if (hour >= 5 && hour < 12) {
    // Morning: 5am - 12pm - Majestic sunrise with mist-covered valleys
    return {
      greeting: "Good Morning",
      image: "https://images.pexels.com/photos/733168/pexels-photo-733168.jpeg?auto=compress&cs=tinysrgb&w=1280&h=720&fit=crop",
      gradientOverlay: "from-[#1A1D1A]/20 via-transparent to-[#FAF9F7]/80",
      mood: "morning"
    };
  } else if (hour >= 12 && hour < 17) {
    // Afternoon: 12pm - 5pm - Serene lake with sun through trees
    return {
      greeting: "Good Afternoon",
      image: "https://images.unsplash.com/photo-1699493840010-0f92a41d1043?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxhZnRlcm5vb24lMjBzdW5zaGluZSUyMGZvcmVzdCUyMGxpZ2h0JTIwcmF5cyUyMHBlYWNlZnVsfGVufDB8fHx8MTc3Mzk5MTg1NHww&ixlib=rb-4.1.0&q=85",
      gradientOverlay: "from-[#1A1D1A]/20 via-transparent to-[#FAF9F7]/80",
      mood: "afternoon"
    };
  } else {
    // Evening: 5pm - 5am - Vibrant mountain silhouette sunset
    return {
      greeting: "Good Evening",
      image: "https://images.pexels.com/photos/7071568/pexels-photo-7071568.jpeg?auto=compress&cs=tinysrgb&w=1280&h=720&fit=crop",
      gradientOverlay: "from-[#1A1D1A]/20 via-transparent to-[#FAF9F7]/80",
      mood: "evening"
    };
  }
};

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.1
    }
  }
};

const cardVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 }
};

// Locked card overlay for areas the user doesn't have access to.
// Clicking routes to the Coming Soon page with the area key for contextual copy.
const LockedCard = ({ children, area, onTap }) => {
  const navigate = useNavigate();
  const handleTap = () => {
    if (onTap) return onTap();
    navigate(`/coming-soon?area=${encodeURIComponent(area || "default")}`);
  };
  return (
    <div
      className="relative cursor-pointer"
      onClick={handleTap}
      data-testid={`locked-card-${area || "default"}`}
    >
      <div className="pointer-events-none opacity-40">{children}</div>
      <div className="absolute inset-0 flex items-center justify-end pr-6 pointer-events-none">
        <div
          className="flex items-center gap-2 px-3 py-1.5 rounded-full"
          style={{
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Lock size={12} style={{ color: "rgba(255,255,255,0.5)" }} />
          <span
            className="text-[10px] uppercase tracking-[0.2em]"
            style={{ color: "rgba(255,255,255,0.5)" }}
          >
            Opening soon
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── Premium card lift recipe ─────────────────────────────────────────
// Simple, premium aesthetic — solid base color (no two-tone gradient drift)
// + a subtle gold lining and soft accent glow. The accent rgb defines the
// card's identity (Aurum blue, Networker green, Abundance gold, Performance
// green, etc.). `goldKiss` is kept as a parameter for compatibility but no
// longer adds an inner gradient stop — every card is now uniformly clean.
const cardLift = ({ accent = "203,161,83", goldKiss = false } = {}) => {
  // Base color: a deep, calm wash of the accent over the app's near-black.
  // No multi-stop gradient — keeps cards feeling settled and cohesive.
  const baseTint = `rgba(${accent},0.07)`;
  // Soft gold lining — the "lovely subtle gold" the user asked for. When
  // `goldKiss` is true we lift the gold a touch so the lining reads more
  // clearly against blue/green-tinted accents.
  const goldEdge = goldKiss
    ? "rgba(241,210,156,0.32)"
    : "rgba(241,210,156,0.22)";
  return {
    background: `linear-gradient(180deg, ${baseTint} 0%, rgba(10,10,8,1) 100%)`,
    border: `1px solid ${goldEdge}`,
    boxShadow: `0 0 32px rgba(${accent},0.06), 0 0 0 1px rgba(241,210,156,0.04), inset 0 1px 0 rgba(241,210,156,0.04)`,
  };
};

export const HomePage = () => {
  const { user } = useAuth();
  const { isTeenMode, colors, isDarkMode } = useTeenMode();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alignmentData, setAlignmentData] = useState(null);
  const [showMilestone, setShowMilestone] = useState(false);
  const [milestoneStreak, setMilestoneStreak] = useState(null);
  const [spinalMilestone, setSpinalMilestone] = useState(null);
  const [showAlignmentCheck, setShowAlignmentCheck] = useState(false);
  const [access, setAccess] = useState(null);
  const [collectiveSavedToday, setCollectiveSavedToday] = useState(null);
  const navigate = useNavigate();
  
  // Dynamic theme colors based on teen mode
  const bgColor = isDarkMode ? colors.base : '#FAF9F7';
  const textColor = isDarkMode ? colors.text : '#1A1D1A';
  const textSecondaryColor = isDarkMode ? colors.textSecondary : '#7A7F7A';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [homeRes, alignmentRes, accessRes, collectiveSavedRes] = await Promise.all([
          homeAPI.getData(),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/alignment/today`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` }
          }).then(r => r.json()).catch(() => null),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/access`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` }
          }).then(r => r.json()).catch(() => null),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/collective/affirmations/saved`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` }
          }).then(r => r.ok ? r.json() : null).catch(() => null)
        ]);
        setData(homeRes.data);
        if (accessRes?.areas) setAccess(accessRes.areas);
        setAlignmentData(alignmentRes);
        // Pluck the most-recent global save from today (community-scoped saves
        // surface inside their own community area instead).
        const todayKey = new Date().toISOString().slice(0, 10);
        const savedToday = (collectiveSavedRes?.saved || []).find(
          (s) => s.date === todayKey && !s.community_id
        );
        if (savedToday) setCollectiveSavedToday(savedToday);
        
        // Check for streak milestones (7, 14, 30, 60, 90)
        const currentStreak = homeRes.data?.presence_streak?.current || 0;
        const milestones = [7, 14, 30, 60, 90];
        const lastShownMilestone = parseInt(localStorage.getItem('lastShownMilestone') || '0');
        
        for (const milestone of milestones) {
          if (currentStreak >= milestone && lastShownMilestone < milestone) {
            setMilestoneStreak(milestone);
            setShowMilestone(true);
            localStorage.setItem('lastShownMilestone', milestone.toString());
            break;
          }
        }

        // Spinal Healing milestone — only fires when a new
        // streak/session/family threshold is crossed AND has never
        // been acknowledged before (server tracks per-user).
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/healing/milestones/check`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` },
        })
          .then((r) => (r.ok ? r.json() : null))
          .then((res) => {
            if (res?.milestone) setSpinalMilestone(res.milestone);
          })
          .catch(() => { /* offline — no bloom this session */ });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <PageWrapper>
        <div className="flex items-center justify-center h-screen" style={{ backgroundColor: bgColor }}>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center"
          >
            <div className="w-12 h-12 border-2 border-[#2D4A2A] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="tracking-wide" style={{ color: textSecondaryColor }}>Preparing your retreat...</p>
          </motion.div>
        </div>
      </PageWrapper>
    );
  }

  // Get the current time environment
  const timeEnvironment = getTimeEnvironment();

  return (
    <PageWrapper>
      <motion.div 
        initial="initial"
        animate="animate"
        variants={staggerContainer}
        className="min-h-screen"
        style={{ backgroundColor: bgColor, color: textColor }}
      >
        {/* Hero Section - Dynamic Time-of-Day Environment */}
        <motion.section 
          variants={fadeInUp}
          className="relative h-[50vh] min-h-[400px] overflow-hidden"
        >
          {/* Dynamic Background - changes with time of day */}
          <motion.div 
            key={timeEnvironment.mood}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${timeEnvironment.image})` }}
          >
            {/* Time-appropriate gradient overlay */}
            <div className={`absolute inset-0 bg-gradient-to-b ${timeEnvironment.gradientOverlay}`} />
            <div 
              className="absolute inset-0 bg-gradient-to-t to-transparent"
              style={{ 
                background: `linear-gradient(to top, ${bgColor}, ${bgColor}33, transparent)` 
              }}
            />
          </motion.div>

          {/* Content */}
          <div className="relative h-full flex flex-col justify-between px-6 pt-8 pb-12 max-w-lg mx-auto">
            {/* Top Bar - Minimal */}
            <div className="flex items-center justify-between">
              <Logo size="sm" variant="light" />
              <div className="flex items-center gap-2">
                {/* Atelier nav badge — self-gates on entitlement */}
                <AtelierNavBadge />
                {/* Daily Essentials — the sun · daily rhythm habits */}
                <button
                  onClick={() => navigate("/daily-essentials")}
                  className="w-10 h-10 bg-amber-500/25 backdrop-blur-md rounded-full flex items-center justify-center text-amber-100 border border-amber-300/30 hover:bg-amber-500/35 transition-all"
                  data-testid="daily-essentials-nav"
                  title="Daily Essentials"
                >
                  <Sun size={18} />
                </button>
                {/* Sleep Biohacking — phase 1 entry */}
                <button
                  onClick={() => navigate("/biohacking/sleep")}
                  className="w-10 h-10 bg-indigo-500/25 backdrop-blur-md rounded-full flex items-center justify-center text-indigo-100 border border-indigo-300/30 hover:bg-indigo-500/35 transition-all"
                  data-testid="sleep-biohacking-nav"
                  title="Sleep Biohacking"
                >
                  <Moon size={18} />
                </button>
                {/* Map Button */}
                <button 
                  onClick={() => navigate("/map")}
                  className="w-10 h-10 bg-white/25 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/30 hover:bg-white/35 transition-all"
                  data-testid="map-button"
                  title="The Reclaim Map"
                >
                  <Compass size={18} />
                </button>
                <NotificationBell />
                <button
                  onClick={() => navigate("/account")}
                  className="w-10 h-10 bg-white/25 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/30 hover:bg-white/35 transition-all"
                  data-testid="settings-btn"
                  title="Settings & Sign out"
                  aria-label="Settings and sign out"
                >
                  <Settings size={18} />
                </button>
              </div>
            </div>

            {/* Greeting - Centered & Peaceful */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="text-center space-y-3"
            >
              <p className="text-white/70 text-sm font-medium uppercase tracking-[0.3em]">
                {timeEnvironment.greeting}
              </p>
              <h1 className="font-heading text-4xl md:text-5xl text-white drop-shadow-lg">
                {data?.user_name || user?.name}
              </h1>
            </motion.div>

            {/* Spacer */}
            <div />
          </div>
        </motion.section>

        {/* D3.60 — Carried Line Hero · the genuine "first thing you see
            today" surface when a member has an active carried compass
            from yesterday's immersion. Lives ABOVE the main content
            wrapper so it bleeds full-width into the time-of-day banner. */}
        <CarriedLineHero />

        {/* Main Content - Calm Flow */}
        <div className="px-6 py-8 space-y-8 max-w-lg mx-auto -mt-4">

          {/* Window Picker — jump to Aurum, Golf, Networker, Inner Path, Gym, Teens */}
          <motion.section variants={cardVariants} data-testid="home-window-picker-section">
            <WindowPicker darkContext={isDarkMode} />
          </motion.section>

          {/* Soulumo Group tiles — cross-property doorways to Soulumo,
              Foundations and One Million Lights. Sits at the top of
              the home column so it's visible without scrolling. */}
          <motion.section variants={cardVariants} data-testid="home-group-tiles-section">
            <ReclaimGroupTiles />
          </motion.section>

          {/* Magical Leader Flow · Phase 4 — glowing banner shows when
              any of the user's teams has an unwatched daily transmission. */}
          <motion.section variants={cardVariants} data-testid="home-team-alignment-banner-section">
            <TeamAlignmentBanner />
          </motion.section>

          {/* Persistent Team Alignment card — direct link to the hub
              for anyone who belongs to a team. Visible regardless of
              whether today's transmission is unwatched. */}
          <motion.section variants={cardVariants} data-testid="home-team-alignment-card-section">
            <TeamAlignmentHomeCard />
          </motion.section>

          {/* Reclaim Partner Pack — gates Partner Status. Surfaces a
              quiet card with the user's current pack state. */}
          <motion.section variants={cardVariants} data-testid="home-partner-pack-section">
            <PartnerPackBanner />
          </motion.section>

          {/* 0. Alignment Check - Primary Daily Entry Point */}
          <motion.section variants={cardVariants}>
            {alignmentData?.completed ? (
              // Completed state - compact card
              <Card 
                className="overflow-hidden bg-gradient-to-br from-[#4A6741]/10 to-[#4A6741]/5 border border-[#4A6741]/20"
                data-testid="alignment-check-completed-card"
              >
                <div className="p-5">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#4A6741] to-[#3D5A35] rounded-xl flex items-center justify-center shadow-md">
                      <CheckCircle2 size={22} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[#4A6741] font-medium">Today's Alignment Complete</p>
                      <p className="text-[#7A7F7A] text-sm mt-0.5">
                        {alignmentData.alignment?.state_word || "Aligned"} → {alignmentData.alignment?.desired_state?.replace('_', ' ') || "Balance"}
                      </p>
                    </div>
                    <button 
                      onClick={() => setShowAlignmentCheck(true)}
                      className="text-[#4A6741]/60 hover:text-[#4A6741] text-sm font-medium"
                    >
                      Redo
                    </button>
                  </div>
                </div>
              </Card>
            ) : (
              // Not completed - prominent action card with natural forest green theme
              <Card 
                className="overflow-hidden bg-gradient-to-br from-[#2D4A2A] via-[#3D5A35] to-[#4A6741] border-0 shadow-lg"
                interactive
                onClick={() => setShowAlignmentCheck(true)}
                data-testid="alignment-check-card"
              >
                <div className="p-6">
                  <div className="flex items-center gap-5">
                    <motion.div 
                      className="relative"
                      animate={{ 
                        scale: [1, 1.05, 1],
                      }}
                      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <div className="w-14 h-14 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg">
                        <Compass size={26} className="text-white" />
                      </div>
                      {/* Soft glow */}
                      <div className="absolute -inset-1 bg-white/10 rounded-2xl blur-md -z-10" />
                    </motion.div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-white mb-1">
                        Alignment Check
                      </h3>
                      <p className="text-white/70 text-sm">
                        Take a moment to check in with yourself
                      </p>
                    </div>
                    <ChevronRight size={20} className="text-white/50" />
                  </div>
                </div>
              </Card>
            )}
          </motion.section>

          {/* Integration Journey Card (Inner Circle Only) */}
          <motion.section variants={cardVariants}>
            {access && !access.integration_program?.unlocked ? (
              <LockedCard area="integration_program">
                <IntegrationCard />
              </LockedCard>
            ) : (
              <IntegrationCard />
            )}
          </motion.section>

          {/* Living Vision Board V2 - Premium Subconscious Conditioning */}
          <motion.section variants={cardVariants}>
            {access && !access.abundance?.unlocked ? (
              <LockedCard area="abundance">
                <Card
                  className="overflow-hidden bg-gradient-to-br from-[#1A1A18] via-[#252520] to-[#1A1A18] border border-[#3A3A35] shadow-xl"
                  data-testid="living-vision-board-card"
                >
                  <div className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-[#CBA153]/20 to-[#4A6741]/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-[#CBA153]/30">
                        <Sparkles size={26} className="text-[#CBA153]" />
                      </div>
                      <div className="flex-1">
                        <p className="text-[#9E9A90] text-[10px] uppercase tracking-[0.2em] font-light mb-1">
                          Reclaim Our Abundance
                        </p>
                        <h3 className="text-[#F2EFE9] font-semibold text-lg font-['Playfair_Display']">Living Vision Board</h3>
                        <p className="text-[#9E9A90] text-sm mt-0.5 font-light">
                          Step into your future reality
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              </LockedCard>
            ) : (
            <Card
              className="overflow-hidden shadow-xl"
              style={cardLift({ accent: "203,161,83", goldKiss: true })}
              interactive
              onClick={() => navigate("/abundance/living-vision-board")}
              data-testid="living-vision-board-card"
            >
              <div className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#CBA153]/20 to-[#4A6741]/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-[#CBA153]/35">
                    <Sparkles size={26} className="text-[#CBA153]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-[0.32em] font-medium mb-1" style={{ color: "rgba(203,161,83,0.70)" }}>
                      Reclaim Our Abundance
                    </p>
                    <h3 className="text-lg font-light italic" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.95)" }}>Living Vision Board</h3>
                    <p className="text-sm mt-0.5 font-light" style={{ color: "rgba(255,255,255,0.55)" }}>
                      Step into your future reality
                    </p>
                  </div>
                  <ChevronRight size={20} className="text-[#CBA153]/50" />
                </div>
              </div>
            </Card>
            )}
          </motion.section>

          {/* Instant Alignment — Audio-led eyes-closed experience */}
          <motion.section variants={cardVariants}>
            <Card
              className="overflow-hidden border border-[#2A2520] shadow-xl"
              style={{ background: 'radial-gradient(ellipse at 50% 60%, rgba(30,22,14,1) 0%, rgba(18,18,16,1) 80%)' }}
              interactive
              onClick={() => navigate("/instant-alignment")}
              data-testid="instant-alignment-card"
            >
              <div className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center border border-[#D2AF82]/15" style={{ background: 'radial-gradient(circle, rgba(210,175,130,0.08) 0%, transparent 70%)' }}>
                    <Waves size={24} strokeWidth={1.2} className="text-[#D2AF82]/60" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[#9E9A90] text-[10px] uppercase tracking-[0.25em] font-light mb-1">
                      Instant Alignment
                    </p>
                    <h3 className="text-[#F2EFE9]/80 text-lg font-light font-['Playfair_Display'] italic">
                      Step into the feeling
                    </h3>
                    <p className="text-[#9E9A90]/60 text-sm mt-0.5 font-light">
                      Eyes closed, audio-led
                    </p>
                  </div>
                  <ChevronRight size={20} className="text-[#D2AF82]/30" />
                </div>
              </div>
            </Card>
          </motion.section>

          {/* Living Manifestation — user-created audio reality */}
          <motion.section variants={cardVariants}>
            {access && !access.abundance?.unlocked ? (
              <LockedCard area="abundance">
                <Card
                  className="overflow-hidden border shadow-xl"
                  style={{
                    background: "linear-gradient(145deg, rgba(203,161,83,0.08) 0%, rgba(14,14,12,1) 100%)",
                    borderColor: "rgba(203,161,83,0.18)",
                  }}
                  data-testid="living-manifestation-card"
                >
                  <div className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: "radial-gradient(circle, rgba(203,161,83,0.15) 0%, transparent 70%)", border: "1px solid rgba(203,161,83,0.2)" }}>
                        <Sparkles size={24} strokeWidth={1.2} style={{ color: "rgba(203,161,83,0.7)" }} />
                      </div>
                      <div className="flex-1">
                        <p className="text-[10px] uppercase tracking-[0.25em] font-light mb-1" style={{ color: "rgba(203,161,83,0.5)" }}>
                          Living Manifestation
                        </p>
                        <h3 className="text-lg font-light italic" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.8)" }}>
                          Your reality, in your own words
                        </h3>
                        <p className="text-sm mt-0.5 font-light" style={{ color: "rgba(203,161,83,0.35)" }}>
                          Write it. Speak it. Live it.
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              </LockedCard>
            ) : (
            <Card
              className="overflow-hidden shadow-xl"
              style={cardLift({ accent: "203,161,83", goldKiss: true })}
              interactive
              onClick={() => navigate("/abundance/manifestations")}
              data-testid="living-manifestation-card"
            >
              <div className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: "radial-gradient(circle, rgba(203,161,83,0.18) 0%, transparent 70%)", border: "1px solid rgba(203,161,83,0.30)" }}>
                    <Sparkles size={24} strokeWidth={1.2} style={{ color: "rgba(203,161,83,0.85)" }} />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-[0.32em] font-medium mb-1" style={{ color: "rgba(203,161,83,0.70)" }}>
                      Living Manifestation
                    </p>
                    <h3 className="text-lg font-light italic" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.95)" }}>
                      Your reality, in your own words
                    </h3>
                    <p className="text-sm mt-0.5 font-light" style={{ color: "rgba(255,255,255,0.55)" }}>
                      Write it. Speak it. Live it.
                    </p>
                  </div>
                  <ChevronRight size={20} style={{ color: "rgba(203,161,83,0.45)" }} />
                </div>
              </div>
            </Card>
            )}
          </motion.section>

          {/* Reclaim Performance — Golf */}
          <motion.section variants={cardVariants}>
            {access && !access.performance?.unlocked ? (
              <LockedCard area="performance">
                <Card className="overflow-hidden border border-[#1a2e1a] shadow-xl"
                  style={{ background: 'linear-gradient(145deg, rgba(10,20,10,1) 0%, rgba(14,14,12,1) 100%)' }}
                  data-testid="golf-performance-card"
                >
                  <div className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center border border-[#4a7a4a]/20" style={{ background: 'radial-gradient(circle, rgba(34,85,34,0.12) 0%, transparent 70%)' }}>
                        <Target size={24} strokeWidth={1.2} style={{ color: 'rgba(120,180,120,0.6)' }} />
                      </div>
                      <div className="flex-1">
                        <p className="text-[10px] uppercase tracking-[0.25em] font-light mb-1" style={{ color: 'rgba(120,180,120,0.5)' }}>Reclaim Performance</p>
                        <h3 className="text-lg font-light italic" style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(245,240,230,0.8)' }}>Golf</h3>
                        <p className="text-sm mt-0.5 font-light" style={{ color: 'rgba(180,160,120,0.4)' }}>Step into the exact state you need</p>
                      </div>
                    </div>
                  </div>
                </Card>
              </LockedCard>
            ) : (
            <Card
              className="overflow-hidden shadow-xl"
              style={cardLift({ accent: "63,143,96", goldKiss: true })}
              interactive
              onClick={() => navigate("/golf")}
              data-testid="golf-performance-card"
            >
              <div className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center border border-[#D7BB7A]/35" style={{ background: 'radial-gradient(circle, rgba(63,143,96,0.22) 0%, transparent 70%)' }}>
                    <Target size={24} strokeWidth={1.2} style={{ color: 'rgba(215,187,122,0.75)' }} />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-[0.32em] font-medium mb-1" style={{ color: 'rgba(215,187,122,0.65)' }}>
                      Reclaim Golf
                    </p>
                    <h3 className="text-lg font-light italic" style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(241,236,221,0.92)' }}>
                      Your Performance Operating System
                    </h3>
                    <p className="text-sm mt-0.5 font-light" style={{ color: 'rgba(123,175,143,0.65)' }}>
                      Inner mastery meets elite performance
                    </p>
                  </div>
                  <ChevronRight size={20} style={{ color: 'rgba(215,187,122,0.40)' }} />
                </div>
              </div>
            </Card>
            )}
          </motion.section>

          {/* Collective Alignment — premium anonymous shared emotional regulation.
              Tile is contextual: if the user has saved today's session, surface a
              "Pick up where you left off" replay tile; otherwise the default
              entry-CTA tile. */}
          <motion.section variants={cardVariants}>
            <Card
              className="overflow-hidden border shadow-xl"
              style={{
                background:
                  "linear-gradient(145deg, rgba(31,58,92,0.95) 0%, rgba(13,31,58,1) 60%, rgba(6,14,31,1) 100%)",
                borderColor: collectiveSavedToday
                  ? "rgba(241,210,156,0.45)"
                  : "rgba(241,210,156,0.18)",
                boxShadow: collectiveSavedToday
                  ? "0 0 50px rgba(241,210,156,0.18)"
                  : undefined,
              }}
              interactive
              onClick={() => navigate(collectiveSavedToday ? "/library/collective" : "/collective")}
              data-testid={collectiveSavedToday ? "collective-alignment-replay-card" : "collective-alignment-card"}
            >
              <div className="p-6">
                <div className="flex items-center gap-4">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center border"
                    style={{
                      background:
                        "radial-gradient(circle, rgba(241,210,156,0.22) 0%, transparent 70%)",
                      borderColor: "rgba(241,210,156,0.25)",
                    }}
                  >
                    {collectiveSavedToday ? (
                      <BookmarkCheck size={22} strokeWidth={1.4} style={{ color: "rgba(241,210,156,0.92)" }} />
                    ) : (
                      <Users size={22} strokeWidth={1.2} style={{ color: "rgba(241,210,156,0.78)" }} />
                    )}
                  </div>
                  <div className="flex-1">
                    <p
                      className="text-[10px] uppercase tracking-[0.32em] font-medium mb-1"
                      style={{ color: "rgba(241,210,156,0.70)" }}
                    >
                      {collectiveSavedToday
                        ? `Your alignment for today${collectiveSavedToday.length_minutes ? ` · ${collectiveSavedToday.length_minutes} min` : ""}`
                        : "Collective Alignment"}
                    </p>
                    <h3
                      className="text-lg font-light italic"
                      style={{
                        fontFamily: "'Playfair Display', serif",
                        color: "rgba(255,255,255,0.92)",
                      }}
                    >
                      {collectiveSavedToday
                        ? "Pick up where you left off"
                        : "You are not carrying life alone"}
                    </h3>
                    <p
                      className="text-sm mt-0.5 font-light truncate"
                      style={{ color: "rgba(255,255,255,0.55)" }}
                    >
                      {collectiveSavedToday?.reframe
                        ? `"${collectiveSavedToday.reframe}"`
                        : "A shared space to release, realign, remember"}
                    </p>
                  </div>
                  <ChevronRight size={20} style={{ color: "rgba(241,210,156,0.42)" }} />
                </div>
              </div>
            </Card>
          </motion.section>

          {/* My Collection — central library of everything the user curates */}
          <motion.section variants={cardVariants}>
            <Card
              className="overflow-hidden cursor-pointer hover:-translate-y-0.5 transition-transform duration-500"
              style={{
                background: "linear-gradient(180deg, rgba(171,144,200,0.07) 0%, rgba(10,10,8,1) 100%)",
                border: "1px solid rgba(241,210,156,0.22)",
                boxShadow: "0 0 32px rgba(171,144,200,0.06), inset 0 1px 0 rgba(241,210,156,0.04)",
              }}
              interactive
              onClick={() => navigate("/library")}
              data-testid="my-collection-card"
            >
              <div className="p-6">
                <div className="flex items-center gap-4">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center border"
                    style={{
                      background: "radial-gradient(circle, rgba(241,210,156,0.18) 0%, transparent 70%)",
                      borderColor: "rgba(241,210,156,0.25)",
                    }}
                  >
                    <Sparkles size={22} strokeWidth={1.2} style={{ color: "rgba(241,210,156,0.85)" }} />
                  </div>
                  <div className="flex-1">
                    <p
                      className="text-[10px] uppercase tracking-[0.32em] font-medium mb-1"
                      style={{ color: "rgba(241,210,156,0.70)" }}
                    >
                      My Collection
                    </p>
                    <h3
                      className="text-lg italic"
                      style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', color: "rgba(245,240,230,0.92)" }}
                    >
                      Everything you've created
                    </h3>
                    <p
                      className="text-sm mt-0.5 font-light truncate"
                      style={{ color: "rgba(255,255,255,0.55)" }}
                    >
                      Audios · Immersions · Images
                    </p>
                  </div>
                  <ChevronRight size={20} style={{ color: "rgba(241,210,156,0.42)" }} />
                </div>
              </div>
            </Card>
          </motion.section>
          <motion.section variants={cardVariants}>
            {access && !access.integration_program?.unlocked ? (
              <LockedCard area="integration_program">
                <Card
                  className="overflow-hidden border border-indigo-500/15 shadow-xl"
                  style={{ background: 'linear-gradient(145deg, rgba(99,102,241,0.08) 0%, rgba(10,13,18,1) 100%)' }}
                  data-testid="integration-program-card"
                >
                  <div className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center border border-indigo-400/15" style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)' }}>
                        <Sparkles size={24} strokeWidth={1.2} style={{ color: 'rgba(165,180,252,0.6)' }} />
                      </div>
                      <div className="flex-1">
                        <p className="text-[10px] uppercase tracking-[0.2em] font-light mb-1" style={{ color: 'rgba(165,180,252,0.5)' }}>
                          21-Day Program
                        </p>
                        <h3 className="text-lg font-light italic" style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(245,240,230,0.8)' }}>
                          The Integration Program
                        </h3>
                        <p className="text-sm mt-0.5 font-light" style={{ color: 'rgba(165,180,252,0.3)' }}>
                          A guided integration journey
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              </LockedCard>
            ) : (
            <Card
              className="overflow-hidden border border-indigo-500/15 shadow-xl"
              style={{ background: 'linear-gradient(145deg, rgba(99,102,241,0.08) 0%, rgba(10,13,18,1) 100%)' }}
              interactive
              onClick={() => navigate("/integration-program")}
              data-testid="integration-program-card"
            >
              <div className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center border border-indigo-400/15" style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)' }}>
                    <Sparkles size={24} strokeWidth={1.2} style={{ color: 'rgba(165,180,252,0.6)' }} />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-[0.2em] font-light mb-1" style={{ color: 'rgba(165,180,252,0.5)' }}>
                      21-Day Program
                    </p>
                    <h3 className="text-lg font-light italic" style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(245,240,230,0.8)' }}>
                      The Integration Program
                    </h3>
                    <p className="text-sm mt-0.5 font-light" style={{ color: 'rgba(165,180,252,0.3)' }}>
                      A guided integration journey
                    </p>
                  </div>
                  <ChevronRight size={20} style={{ color: 'rgba(165,180,252,0.25)' }} />
                </div>
              </div>
            </Card>
            )}
          </motion.section>

          {/* Abundance Journey Program */}
          <motion.section variants={cardVariants}>
            {access && !access.abundance?.unlocked ? (
              <LockedCard area="abundance">
                <Card
                  className="overflow-hidden bg-gradient-to-br from-[#4A6741] via-[#5A7751] to-[#3A5731] border-0 shadow-lg"
                  data-testid="abundance-journey-card"
                >
                  <div className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                        <Compass size={26} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-white font-semibold text-lg">Reclaim Our Abundance</h3>
                        <p className="text-white/70 text-sm mt-0.5">
                          21-day transformation journey
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              </LockedCard>
            ) : (
            <Card
              className="overflow-hidden bg-gradient-to-br from-[#4A6741] via-[#5A7751] to-[#3A5731] border-0 shadow-lg"
              interactive
              onClick={() => navigate("/abundance-journey")}
              data-testid="abundance-journey-card"
            >
              <div className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <Compass size={26} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold text-lg">Reclaim Our Abundance</h3>
                    <p className="text-white/70 text-sm mt-0.5">
                      21-day transformation journey
                    </p>
                  </div>
                  <ChevronRight size={20} className="text-white/50" />
                </div>
              </div>
            </Card>
            )}
          </motion.section>

          {/* Reclaim Journeys — Absorption Engine entry */}
          <motion.section variants={cardVariants}>
            <Card
              className="overflow-hidden border-0 shadow-lg"
              style={{
                background: "linear-gradient(135deg, #FAF7F0 0%, #F0E5D0 55%, #E0CBA8 100%)",
              }}
              interactive
              onClick={() => navigate("/journeys")}
              data-testid="journeys-hub-card"
            >
              <div className="p-6">
                <div className="flex items-center gap-4">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center"
                    style={{ background: "rgba(168,137,92,0.18)", border: "1px solid rgba(168,137,92,0.35)" }}
                  >
                    <Compass size={26} style={{ color: "#8E7349" }} />
                  </div>
                  <div className="flex-1">
                    <p
                      className="text-[10px] uppercase tracking-[0.3em] font-light mb-1"
                      style={{ color: "#A8895C" }}
                    >
                      New
                    </p>
                    <h3
                      className="text-lg italic"
                      style={{ fontFamily: "'Playfair Display', serif", color: "rgba(40,32,26,0.92)", fontWeight: 500 }}
                    >
                      Absorption Engine
                    </h3>
                    <p
                      className="text-sm mt-0.5"
                      style={{ color: "rgba(40,32,26,0.6)", fontFamily: "'Playfair Display', serif", fontStyle: "italic" }}
                    >
                      A calm daily flow that lets it settle into you
                    </p>
                  </div>
                  <ChevronRight size={20} style={{ color: "rgba(40,32,26,0.35)" }} />
                </div>
              </div>
            </Card>
          </motion.section>

          {/* Concierge welcome — only renders if Michael has minted
              a personal welcome audio for this member (Faye, etc.). */}
          <motion.section variants={cardVariants}>
            <WelcomeAudioCard />
          </motion.section>

          {/* Today's Activation — small daily pick from the new
              "Awareness in the everyday" library. Rotates per UTC
              day, deep-links straight into the player. */}
          <motion.section variants={cardVariants}>
            <TodaysActivationPill onOpen={(slug) => navigate(`/reclaim-presence/activation/${slug}`)} />
          </motion.section>

          {/* Reclaim Our Presence - 21 Day Immersion */}
          <motion.section variants={cardVariants}>
            {access && !access.presence?.unlocked ? (
              <LockedCard area="presence">
                <Card
                  className="overflow-hidden bg-gradient-to-br from-emerald-800 via-teal-800 to-emerald-900 border-0 shadow-lg"
                  data-testid="reclaim-presence-card"
                >
                  <div className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                        <Leaf size={26} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-emerald-200/70 text-[10px] uppercase tracking-[0.2em] font-light mb-1">
                          New Pillar
                        </p>
                        <h3 className="text-white font-semibold text-lg">Reclaim Our Presence</h3>
                        <p className="text-white/70 text-sm mt-0.5">
                          21-day immersion into presence
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              </LockedCard>
            ) : (
            <Card
              className="overflow-hidden bg-gradient-to-br from-emerald-800 via-teal-800 to-emerald-900 border-0 shadow-lg"
              interactive
              onClick={() => navigate("/reclaim-presence")}
              data-testid="reclaim-presence-card"
            >
              <div className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <Leaf size={26} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-emerald-200/70 text-[10px] uppercase tracking-[0.2em] font-light mb-1">
                      New Pillar
                    </p>
                    <h3 className="text-white font-semibold text-lg">Reclaim Our Presence</h3>
                    <p className="text-white/70 text-sm mt-0.5">
                      21-day immersion into presence
                    </p>
                  </div>
                  <ChevronRight size={20} className="text-white/50" />
                </div>
              </div>
            </Card>
            )}
          </motion.section>

          {/* Your Living Dream — Phase 1a flagship surface */}
          <motion.section variants={cardVariants} className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-1 h-5 rounded-full"
                   style={{ background: "linear-gradient(to bottom, rgba(215,187,122,0.95), rgba(168,138,76,0.95))" }} />
              <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A7F7A]">
                Your Living Dream
              </h2>
            </div>
            <Card
              className="relative overflow-hidden shadow-lg border-0"
              interactive
              onClick={() => navigate("/living-dream")}
              data-testid="living-dream-home-card"
              style={{
                background: "linear-gradient(135deg, #1A1108 0%, #0F0A05 55%, #060403 100%)",
                boxShadow: "0 22px 48px -24px rgba(215,187,122,0.45), inset 0 1px 0 rgba(255,255,255,0.04)",
              }}
            >
              {/* slow gold horizon */}
              <div aria-hidden className="pointer-events-none absolute inset-0"
                   style={{ background: "radial-gradient(circle at 78% 28%, rgba(215,187,122,0.32) 0%, transparent 58%)" }} />
              <div aria-hidden className="pointer-events-none absolute top-0 inset-x-0 h-px"
                   style={{ background: "linear-gradient(90deg, transparent, rgba(215,187,122,0.6), transparent)" }} />
              <div className="relative p-6 flex items-center gap-5">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                     style={{
                       background: "linear-gradient(135deg, rgba(215,187,122,0.32), rgba(168,138,76,0.10))",
                       boxShadow: "0 10px 30px -12px rgba(215,187,122,0.65), inset 0 1px 0 rgba(255,255,255,0.10)",
                     }}>
                  {/* Single gold orb = dream motif */}
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <defs>
                      <radialGradient id="ld-orb" cx="50%" cy="40%" r="50%">
                        <stop offset="0%" stopColor="#FFE9A8" />
                        <stop offset="55%" stopColor="#D7BB7A" />
                        <stop offset="100%" stopColor="rgba(168,138,76,0)" />
                      </radialGradient>
                    </defs>
                    <circle cx="12" cy="12" r="7" fill="url(#ld-orb)" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-[0.4em] mb-1.5 font-medium" style={{ color: "rgba(215,187,122,0.95)" }}>
                    YOUR LIVING DREAM
                  </p>
                  <h3 className="font-semibold text-xl text-white mb-0.5" style={{ letterSpacing: "-0.012em" }}>
                    The reality you are stepping into.
                  </h3>
                  <p className="text-white/55 text-sm italic"
                     style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "14px" }}>
                    Write your vision · Receive your future identity · Record your voice
                  </p>
                </div>
                <ChevronRight size={22} className="text-white/55" />
              </div>
            </Card>
          </motion.section>

          {/* Rewrite Engine — 6 cinematic rituals (Golf · Clearing · Abundance · Presence · Relationships · Performance) */}
          <motion.section variants={cardVariants} className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-1 h-5 rounded-full"
                   style={{ background: "linear-gradient(to bottom, rgba(215,187,122,0.95), rgba(168,138,76,0.95))" }} />
              <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A7F7A]">
                Rewrite Engine
              </h2>
            </div>
            <Card
              className="relative overflow-hidden shadow-lg border-0"
              interactive
              onClick={() => navigate("/rewrite")}
              data-testid="rewrite-engine-home-card"
              style={{
                background: "linear-gradient(135deg, #14201C 0%, #0A1410 60%, #03070A 100%)",
                boxShadow: "0 22px 48px -24px rgba(215,187,122,0.40), inset 0 1px 0 rgba(255,255,255,0.04)",
              }}
            >
              <div aria-hidden className="pointer-events-none absolute inset-0"
                   style={{ background: "radial-gradient(circle at 22% 28%, rgba(215,187,122,0.30) 0%, transparent 60%)" }} />
              <div aria-hidden className="pointer-events-none absolute top-0 inset-x-0 h-px"
                   style={{ background: "linear-gradient(90deg, transparent, rgba(215,187,122,0.55), transparent)" }} />
              <div className="relative p-6 flex items-center gap-5">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                     style={{
                       background: "linear-gradient(135deg, rgba(215,187,122,0.28), rgba(168,138,76,0.08))",
                       boxShadow: "0 10px 30px -12px rgba(215,187,122,0.55), inset 0 1px 0 rgba(255,255,255,0.10)",
                     }}>
                  {/* Three small orbs representing the multi-domain choice */}
                  <svg width="38" height="38" viewBox="0 0 38 38" fill="none">
                    <defs>
                      <radialGradient id="reh-orb" cx="50%" cy="40%" r="50%">
                        <stop offset="0%" stopColor="#FFE9A8" />
                        <stop offset="55%" stopColor="#D7BB7A" />
                        <stop offset="100%" stopColor="rgba(168,138,76,0)" />
                      </radialGradient>
                    </defs>
                    <circle cx="11" cy="22" r="6" fill="url(#reh-orb)" />
                    <circle cx="22" cy="13" r="6" fill="url(#reh-orb)" />
                    <circle cx="28" cy="24" r="5" fill="url(#reh-orb)" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-[0.4em] mb-1.5 font-medium" style={{ color: "rgba(215,187,122,0.95)" }}>
                    REWRITE ENGINE
                  </p>
                  <h3 className="font-semibold text-xl text-white mb-0.5" style={{ letterSpacing: "-0.012em" }}>
                    Six worlds. One continuous voice.
                  </h3>
                  <p className="text-white/55 text-sm italic"
                     style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "14px" }}>
                    Golf · Clearing · Abundance · Presence · Relationships · Performance
                  </p>
                </div>
                <ChevronRight size={22} className="text-white/55" />
              </div>
            </Card>
          </motion.section>
          <motion.section variants={cardVariants} className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-1 h-5 rounded-full"
                   style={{ background: "linear-gradient(to bottom, rgba(190,205,235,0.8), rgba(160,180,220,0.8))" }} />
              <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A7F7A]">
                Practices
              </h2>
            </div>
            <Card
              className="relative overflow-hidden shadow-lg border-0"
              interactive
              onClick={() => navigate("/practices")}
              data-testid="practices-home-card"
              style={{
                background: "linear-gradient(135deg, #0E1A2C 0%, #0A1424 55%, #050A14 100%)",
                boxShadow: "0 22px 48px -24px rgba(170,195,225,0.4), inset 0 1px 0 rgba(255,255,255,0.04)",
              }}
            >
              {/* slow drift glow */}
              <div aria-hidden className="pointer-events-none absolute inset-0"
                   style={{ background: "radial-gradient(circle at 78% 30%, rgba(190,205,235,0.16) 0%, transparent 55%)" }} />
              <div aria-hidden className="pointer-events-none absolute top-0 inset-x-0 h-px"
                   style={{ background: "linear-gradient(90deg, transparent, rgba(190,205,235,0.5), transparent)" }} />
              <div className="relative p-6 flex items-center gap-5">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                     style={{
                       background: "linear-gradient(135deg, rgba(190,205,235,0.25), rgba(160,180,220,0.06))",
                       boxShadow: "0 10px 30px -12px rgba(190,205,235,0.55), inset 0 1px 0 rgba(255,255,255,0.08)",
                     }}>
                  {/* Three concentric breathing dots = practices motif */}
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="rgba(220,232,250,0.95)" strokeWidth="1.4" strokeLinecap="round">
                    <circle cx="12" cy="12" r="3.2" />
                    <circle cx="12" cy="12" r="6.5" opacity="0.6" />
                    <circle cx="12" cy="12" r="9.8" opacity="0.3" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-[0.4em] mb-1.5 font-medium" style={{ color: "rgba(190,205,235,0.85)" }}>
                    PRACTICES
                  </p>
                  <h3 className="font-semibold text-xl text-white mb-0.5" style={{ letterSpacing: "-0.012em" }}>
                    Return. Reset. Re-centre.
                  </h3>
                  <p className="text-white/55 text-sm italic"
                     style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "14px" }}>
                    Resets · State Tuning · Tapping
                  </p>
                </div>
                <ChevronRight size={22} className="text-white/55" />
              </div>
            </Card>
          </motion.section>

          {/* Reclaim Our Balance - Emotional Reset Journeys */}
          <motion.section variants={cardVariants}>
            {access && !access.healing?.unlocked ? (
              <LockedCard area="healing">
                <Card
                  className="overflow-hidden bg-gradient-to-br from-violet-800 via-purple-800 to-pink-900 border-0 shadow-lg"
                  data-testid="reclaim-balance-card"
                >
                  <div className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                        <Heart size={26} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-pink-200/70 text-[10px] uppercase tracking-[0.2em] font-light mb-1">
                          New Pillar
                        </p>
                        <h3 className="text-white font-semibold text-lg">Reclaim Our Balance</h3>
                        <p className="text-white/70 text-sm mt-0.5">
                          5-day emotional reset journeys
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              </LockedCard>
            ) : (
            <Card
              className="overflow-hidden bg-gradient-to-br from-violet-800 via-purple-800 to-pink-900 border-0 shadow-lg"
              interactive
              onClick={() => navigate("/balance")}
              data-testid="reclaim-balance-card"
            >
              <div className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <Heart size={26} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-pink-200/70 text-[10px] uppercase tracking-[0.2em] font-light mb-1">
                      New Pillar
                    </p>
                    <h3 className="text-white font-semibold text-lg">Reclaim Our Balance</h3>
                    <p className="text-white/70 text-sm mt-0.5">
                      5-day emotional reset journeys
                    </p>
                  </div>
                  <ChevronRight size={20} className="text-white/50" />
                </div>
              </div>
            </Card>
            )}
          </motion.section>

          {/* PRIVATE SUITE - Main Card */}
          <motion.section variants={cardVariants}>
            {access && !access.private_suite?.unlocked ? (
              <LockedCard area="private_suite">
                <Card
                  className="overflow-hidden bg-gradient-to-br from-[#4A3D5A] via-[#5D4E70] to-[#6B5A7D] border-0 shadow-lg"
                  data-testid="private-suite-card"
                >
                  <div className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                        <Sparkles size={26} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-white font-semibold text-lg">Private Suite</h3>
                        <p className="text-white/70 text-sm mt-0.5">
                          Personalised meditation tuned to you
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              </LockedCard>
            ) : (
            <Card
              className="overflow-hidden bg-gradient-to-br from-[#4A3D5A] via-[#5D4E70] to-[#6B5A7D] border-0 shadow-lg"
              interactive
              onClick={() => navigate("/private-suite")}
              data-testid="private-suite-card"
            >
              <div className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <Sparkles size={26} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold text-lg">Private Suite</h3>
                    <p className="text-white/70 text-sm mt-0.5">
                      Personalised meditation tuned to you
                    </p>
                  </div>
                  <ChevronRight size={20} className="text-white/50" />
                </div>
              </div>
            </Card>
            )}
          </motion.section>

          {/* AURUM EDGE — flagship card. Keeps its blue identity, lifted
              with deeper gradient + soft gold accent + ambient glow to
              match the Collective Alignment quality bar. */}
          <motion.section variants={cardVariants}>
            <Card
              className="overflow-hidden border-0 shadow-2xl relative"
              interactive
              onClick={() => navigate("/aurum-edge")}
              data-testid="aurum-edge-card"
              style={{
                background:
                  'linear-gradient(145deg, #1834D6 0%, #122BAB 32%, #0D1F7A 65%, #08123F 100%)',
                boxShadow:
                  '0 20px 60px rgba(24,52,214,0.35), 0 0 100px rgba(241,210,156,0.10), inset 0 1px 0 rgba(255,255,255,0.06)',
                border: '1px solid rgba(241,210,156,0.22)',
              }}
            >
              {/* Soft sunrise-gold halo at the top-right (Aurum = "gilded dawn") */}
              <div
                className="absolute -top-16 -right-12 w-48 h-48 rounded-full pointer-events-none"
                style={{
                  background:
                    'radial-gradient(circle, rgba(241,210,156,0.28) 0%, rgba(160,200,255,0.10) 35%, transparent 70%)',
                  filter: 'blur(2px)',
                }}
              />
              {/* Subtle gold ribbon along the bottom — anchors the gilding */}
              <div
                className="absolute inset-x-0 bottom-0 h-px pointer-events-none"
                style={{
                  background:
                    'linear-gradient(90deg, transparent 0%, rgba(241,210,156,0.45) 50%, transparent 100%)',
                }}
              />
              <div className="p-6 relative">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
                    style={{
                      background:
                        'linear-gradient(145deg, rgba(255,255,255,0.18) 0%, rgba(241,210,156,0.10) 100%)',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(241,210,156,0.32)',
                      boxShadow: '0 0 24px rgba(241,210,156,0.18)',
                    }}
                  >
                    <svg width={26} height={26} viewBox="0 0 64 64">
                      <defs>
                        <linearGradient id="aurumHexGold" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.95" />
                          <stop offset="100%" stopColor="#F5E0AD" stopOpacity="0.85" />
                        </linearGradient>
                      </defs>
                      <path
                        d="M32 4 L56 18 L56 46 L32 60 L8 46 L8 18 Z"
                        fill="rgba(241,210,156,0.10)"
                        stroke="url(#aurumHexGold)"
                        strokeWidth="1.5"
                      />
                      <circle cx="32" cy="32" r="3" fill="#F5E0AD" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p
                      className="text-[10px] uppercase tracking-[0.42em] font-medium mb-1"
                      style={{ color: 'rgba(241,210,156,0.75)' }}
                    >
                      The Edge · Aurum
                    </p>
                    <h3
                      className="font-light tracking-[0.18em] text-2xl"
                      style={{
                        background:
                          'linear-gradient(180deg, #FFFFFF 0%, #F5E0AD 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      AURUM
                    </h3>
                    <p
                      className="text-sm mt-1 font-light italic"
                      style={{
                        color: 'rgba(255,255,255,0.72)',
                        fontFamily: "'Playfair Display', serif",
                      }}
                    >
                      Align with your Aurum reality
                    </p>
                  </div>
                  <ChevronRight size={20} style={{ color: 'rgba(241,210,156,0.55)' }} />
                </div>
              </div>
            </Card>
          </motion.section>

          {/* INNER PATH — sacred / consciousness window. Sibling card
              to Aurum Edge. Deep navy + soft gold + sacred geometry
              flower-of-life ring. Routes to /inner-path. */}
          <motion.section variants={cardVariants}>
            <Card
              className="overflow-hidden border-0 shadow-2xl relative"
              interactive
              onClick={() => navigate("/inner-path")}
              data-testid="inner-path-card"
              style={{
                background:
                  'linear-gradient(145deg, #182742 0%, #0F1D36 32%, #0B1426 65%, #060B17 100%)',
                boxShadow:
                  '0 20px 60px rgba(11,20,38,0.45), 0 0 100px rgba(212,185,120,0.10), inset 0 1px 0 rgba(255,255,255,0.04)',
                border: '1px solid rgba(212,185,120,0.22)',
              }}
            >
              {/* Soft dawn halo top-right */}
              <div
                className="absolute -top-16 -right-12 w-48 h-48 rounded-full pointer-events-none"
                style={{
                  background:
                    'radial-gradient(circle, rgba(212,185,120,0.24) 0%, rgba(201,155,122,0.10) 35%, transparent 70%)',
                  filter: 'blur(2px)',
                }}
              />
              {/* Bottom gold ribbon */}
              <div
                className="absolute inset-x-0 bottom-0 h-px pointer-events-none"
                style={{
                  background:
                    'linear-gradient(90deg, transparent 0%, rgba(212,185,120,0.42) 50%, transparent 100%)',
                }}
              />
              <div className="p-6 relative">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
                    style={{
                      background:
                        'linear-gradient(145deg, rgba(255,255,255,0.10) 0%, rgba(212,185,120,0.10) 100%)',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(212,185,120,0.32)',
                      boxShadow: '0 0 24px rgba(212,185,120,0.16)',
                    }}
                  >
                    {/* Sacred flower-of-life ring — distinct from Aurum's hex */}
                    <svg width={28} height={28} viewBox="0 0 64 64">
                      <defs>
                        <radialGradient id="ipFlowerGold" cx="50%" cy="50%" r="50%">
                          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.95" />
                          <stop offset="100%" stopColor="#D4B978" stopOpacity="0.85" />
                        </radialGradient>
                      </defs>
                      <circle cx="32" cy="32" r="14" fill="none" stroke="url(#ipFlowerGold)" strokeWidth="1.3" />
                      {[0, 60, 120, 180, 240, 300].map((deg) => {
                        const r = 8;
                        const cx = 32 + r * Math.cos((deg * Math.PI) / 180);
                        const cy = 32 + r * Math.sin((deg * Math.PI) / 180);
                        return (
                          <circle key={deg} cx={cx} cy={cy} r="6" fill="none"
                                  stroke="url(#ipFlowerGold)" strokeWidth="1" opacity="0.75" />
                        );
                      })}
                      <circle cx="32" cy="32" r="2.2" fill="#D4B978" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p
                      className="text-[10px] uppercase tracking-[0.42em] font-medium mb-1"
                      style={{ color: 'rgba(212,185,120,0.78)' }}
                    >
                      Inner Path · The Sacred Layer
                    </p>
                    <h3
                      className="font-light tracking-[0.04em] text-2xl"
                      style={{
                        fontFamily: '"Cormorant Garamond", Georgia, serif',
                        background:
                          'linear-gradient(180deg, #FFFFFF 0%, #D4B978 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      Return to yourself
                    </h3>
                    <p
                      className="text-xs mt-1 italic"
                      style={{
                        color: 'rgba(212,185,120,0.55)',
                        fontFamily: '"Cormorant Garamond", Georgia, serif',
                      }}
                    >
                      Presence · Awareness · Alignment
                    </p>
                  </div>
                  <ChevronRight size={20} style={{ color: 'rgba(212,185,120,0.55)' }} />
                </div>
              </div>
            </Card>
          </motion.section>

          {/* GLOBAL ALIGNMENT — sibling card to Inner Path. Links to
              the Collective Alignment experience (/collective) that
              lives inside the Inner Path section — the planetary /
              "we are one field" entry-point. Earth-toned palette
              (deep moss + warm gold) distinguishes it visually from
              Inner Path's deep-navy sacred geometry. */}
          <motion.section variants={cardVariants}>
            <Card
              className="overflow-hidden border-0 shadow-2xl relative"
              interactive
              onClick={() => navigate("/collective")}
              data-testid="global-alignment-card"
              style={{
                background:
                  'linear-gradient(145deg, #1F3A2A 0%, #16291F 32%, #0F1E16 65%, #07120D 100%)',
                boxShadow:
                  '0 20px 60px rgba(11,30,22,0.45), 0 0 100px rgba(212,185,120,0.10), inset 0 1px 0 rgba(255,255,255,0.04)',
                border: '1px solid rgba(212,185,120,0.22)',
              }}
            >
              {/* Soft warm halo top-right — earth meets gold */}
              <div
                className="absolute -top-16 -right-12 w-48 h-48 rounded-full pointer-events-none"
                style={{
                  background:
                    'radial-gradient(circle, rgba(212,185,120,0.22) 0%, rgba(94,138,99,0.12) 35%, transparent 70%)',
                  filter: 'blur(2px)',
                }}
              />
              {/* Bottom gold ribbon — matches the Inner Path sibling */}
              <div
                className="absolute inset-x-0 bottom-0 h-px pointer-events-none"
                style={{
                  background:
                    'linear-gradient(90deg, transparent 0%, rgba(212,185,120,0.42) 50%, transparent 100%)',
                }}
              />
              <div className="p-6 relative">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
                    style={{
                      background:
                        'linear-gradient(145deg, rgba(255,255,255,0.10) 0%, rgba(94,138,99,0.18) 100%)',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(212,185,120,0.32)',
                      boxShadow: '0 0 24px rgba(212,185,120,0.16)',
                    }}
                  >
                    <Globe size={26} strokeWidth={1.2} style={{ color: "#D4B978" }} />
                  </div>
                  <div className="flex-1">
                    <p
                      className="text-[10px] uppercase tracking-[0.42em] font-medium mb-1"
                      style={{ color: 'rgba(212,185,120,0.78)' }}
                    >
                      Global Alignment · The Shared Field
                    </p>
                    <h3
                      className="font-light tracking-[0.04em] text-2xl"
                      style={{
                        fontFamily: '"Cormorant Garamond", Georgia, serif',
                        background:
                          'linear-gradient(180deg, #FFFFFF 0%, #D4B978 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      We move as one
                    </h3>
                    <p
                      className="text-xs mt-1 italic"
                      style={{
                        color: 'rgba(212,185,120,0.55)',
                        fontFamily: '"Cormorant Garamond", Georgia, serif',
                      }}
                    >
                      One breath · one field · one frequency
                    </p>
                  </div>
                  <ChevronRight size={20} style={{ color: 'rgba(212,185,120,0.55)' }} />
                </div>
              </div>
            </Card>
          </motion.section>

          {/* 1. Presence Streak - Unified Daily Engagement */}
          {data?.presence_streak && data.presence_streak.current > 0 && (
            <motion.section variants={cardVariants}>
              <Card 
                className="overflow-hidden bg-gradient-to-br from-[#2D4A2A]/5 via-[#F8F7F5] to-[#B8956C]/5 border border-[#E8E6E2]"
                data-testid="presence-streak-card"
              >
                <div className="p-6">
                  <div className="flex items-center gap-5">
                    {/* Streak Icon */}
                    <motion.div 
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
                      className="relative"
                    >
                      <div className="w-16 h-16 bg-gradient-to-br from-[#B8956C] to-[#9A7B56] rounded-2xl flex items-center justify-center shadow-lg">
                        <Leaf size={28} className="text-white" />
                      </div>
                      {/* Glow effect */}
                      <div className="absolute -inset-1 bg-[#B8956C]/20 rounded-2xl blur-md -z-10" />
                    </motion.div>
                    
                    {/* Streak Info */}
                    <div className="flex-1">
                      <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="flex items-baseline gap-2"
                      >
                        <span className="font-heading text-4xl text-[#2D4A2A]">
                          {data.presence_streak.current}
                        </span>
                        <span className="text-[#7A7F7A] font-medium">
                          {data.presence_streak.message}
                        </span>
                      </motion.div>
                      
                      {/* Today's Activities */}
                      <div className="flex items-center gap-3 mt-2">
                        {data.presence_streak.activities_today?.presence && (
                          <motion.span 
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.4 }}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#B8956C]/15 rounded-full text-xs text-[#9A7B56] font-medium"
                          >
                            <CheckCircle2 size={11} /> Presence
                          </motion.span>
                        )}
                        {data.presence_streak.activities_today?.alignment && (
                          <motion.span 
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.5 }}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#2D4A2A]/10 rounded-full text-xs text-[#2D4A2A] font-medium"
                          >
                            <CheckCircle2 size={11} /> Alignment
                          </motion.span>
                        )}
                        {data.presence_streak.activities_today?.challenge && (
                          <motion.span 
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.6 }}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#4A5A68]/10 rounded-full text-xs text-[#4A5A68] font-medium"
                          >
                            <CheckCircle2 size={11} /> Challenge
                          </motion.span>
                        )}
                        {!data.presence_streak.today_completed && (
                          <span className="text-xs text-[#9A9F9A] italic">
                            Complete any activity to continue
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Longest Streak */}
                  {data.presence_streak.longest > data.presence_streak.current && (
                    <div className="mt-4 pt-4 border-t border-[#E8E6E2]/60 flex items-center justify-between text-sm">
                      <span className="text-[#9A9F9A]">Longest streak</span>
                      <span className="text-[#7A7F7A] font-medium">{data.presence_streak.longest} days</span>
                    </div>
                  )}
                </div>
              </Card>
            </motion.section>
          )}
          
          {/* 1. Daily Presence - Primary Action */}
          <motion.section variants={cardVariants}>
            {access && !access.presence?.unlocked ? (
              <LockedCard area="presence">
                <Card
                  className="overflow-hidden"
                  data-testid="daily-presence-card"
                >
                  <div className="p-7">
                    <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-[#B8956C]/15 rounded-xl flex items-center justify-center">
                            <Sparkles size={22} className="text-[#9A7B56]" />
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-[0.2em] text-[#7A7F7A] font-semibold">Daily Presence</p>
                          </div>
                        </div>
                      </div>
                      <p className="font-heading text-2xl text-[#1A1D1A] leading-relaxed">
                        A daily moment of presence
                      </p>
                    </div>
                  </div>
                </Card>
              </LockedCard>
            ) : (
            <Card
              className={`overflow-hidden ${data?.presence?.completed ? 'bg-gradient-to-br from-[#2D4A2A]/8 to-[#2D4A2A]/15' : ''}`}
              interactive={!data?.presence?.completed}
              onClick={() => !data?.presence?.completed && navigate("/presence")}
              data-testid="daily-presence-card"
            >
              <div className="p-7">
                {data?.presence?.completed ? (
                  <div className="flex items-center gap-5">
                    <motion.div 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 300 }}
                      className="w-14 h-14 bg-gradient-to-br from-[#2D4A2A] to-[#3D5A34] rounded-2xl flex items-center justify-center shadow-lg"
                    >
                      <CheckCircle2 size={24} className="text-white" />
                    </motion.div>
                    <div className="flex-1">
                      <p className="text-[#2D4A2A] font-medium text-lg">Today's ritual complete</p>
                      <p className="text-[#6B7280] text-sm mt-0.5">See you tomorrow</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-[#B8956C]/15 rounded-xl flex items-center justify-center">
                          <Sparkles size={22} className="text-[#9A7B56]" />
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-[0.2em] text-[#7A7F7A] font-semibold">Daily Presence</p>
                          {data?.presence?.streak > 0 && (
                            <div className="flex items-center gap-1 text-[#B8956C] mt-0.5">
                              <Flame size={12} />
                              <span className="text-xs font-semibold">{data.presence.streak} day streak</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <p className="font-heading text-2xl text-[#1A1D1A] leading-relaxed">
                      "{data?.presence?.prompt?.text}"
                    </p>
                    
                    <Button variant="primary" className="w-full" size="lg">
                      Begin Reflection
                    </Button>
                  </div>
                )}
              </div>
            </Card>
            )}
          </motion.section>

          {/* Parent Challenge - For parent accounts - Prominent Position */}
          {(user?.account_type === "parent" || user?.parent_challenge_unlocked) && (
            <motion.section variants={cardVariants}>
              <Card 
                className="overflow-hidden bg-gradient-to-r from-[#4A6741] to-[#3D5A35] border-0"
                interactive
                onClick={() => navigate("/parent/challenge")}
                data-testid="parent-challenge-card-top"
              >
                <div className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center shadow-lg">
                      <Heart size={26} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-semibold text-lg">21 Days to Calm & Connection</p>
                      <p className="text-white/70 text-sm mt-0.5">Your gentle parenting journey</p>
                    </div>
                    <ChevronRight size={20} className="text-white/60" />
                  </div>
                </div>
              </Card>
            </motion.section>
          )}

          {/* 2. Challenge of the Week */}
          {data?.featured_challenge && (
            <motion.section variants={cardVariants} className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-1 h-5 bg-[#B8956C] rounded-full" />
                <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A7F7A]">
                  Challenge of the Week
                </h2>
              </div>
              {access && !access.challenges?.unlocked ? (
                <LockedCard area="challenges">
                  <Card
                    className="overflow-hidden"
                    data-testid="challenge-of-week-card"
                  >
                    <div className="h-40 bg-gradient-to-br from-[#2D4A2A] to-[#1F3620] relative overflow-hidden">
                      {data.featured_challenge.image_url ? (
                        <img
                          src={data.featured_challenge.image_url}
                          alt={data.featured_challenge.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Trophy className="text-white/30" size={48} />
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <h3 className="font-heading text-xl text-[#1A1D1A] mb-2">
                        {data.featured_challenge.title}
                      </h3>
                      <p className="text-sm text-[#4A4F4A] leading-relaxed">
                        {data.featured_challenge.description}
                      </p>
                    </div>
                  </Card>
                </LockedCard>
              ) : (
              <Card 
                className="overflow-hidden"
                interactive
                onClick={() => navigate(`/challenges/${data.featured_challenge.id}`)}
                data-testid="challenge-of-week-card"
              >
                {/* Image Header */}
                <div className="h-40 bg-gradient-to-br from-[#2D4A2A] to-[#1F3620] relative overflow-hidden">
                  {data.featured_challenge.image_url ? (
                    <img 
                      src={data.featured_challenge.image_url} 
                      alt={data.featured_challenge.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Trophy className="text-white/30" size={48} />
                    </div>
                  )}
                  
                  {/* Duration Badge */}
                  <div className="absolute top-4 right-4 px-3 py-1.5 bg-black/30 backdrop-blur-md rounded-full text-white text-xs font-semibold flex items-center gap-1.5">
                    <Clock size={12} />
                    {data.featured_challenge.duration} days
                  </div>

                  {/* Status */}
                  {data.featured_challenge.user_progress?.status === "in_progress" && (
                    <div className="absolute top-4 left-4 px-3 py-1.5 bg-[#B8956C] rounded-full text-white text-xs font-semibold">
                      In Progress
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="font-heading text-xl text-[#1A1D1A] mb-2">
                    {data.featured_challenge.title}
                  </h3>
                  <p className="text-sm text-[#4A4F4A] leading-relaxed mb-4">
                    {data.featured_challenge.description}
                  </p>

                  {/* Progress or CTA */}
                  {data.featured_challenge.user_progress?.status === "in_progress" ? (
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-[#7A7F7A]">Day {data.featured_challenge.user_progress.completed_days?.length || 0}</span>
                        <span className="text-[#2D4A2A] font-semibold">
                          {Math.round((data.featured_challenge.user_progress.completed_days?.length || 0) / data.featured_challenge.duration * 100)}%
                        </span>
                      </div>
                      <div className="h-2 bg-[#F0EFEB] rounded-full overflow-hidden">
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: `${(data.featured_challenge.user_progress.completed_days?.length || 0) / data.featured_challenge.duration * 100}%` }}
                          className="h-full bg-[#2D4A2A] rounded-full"
                        />
                      </div>
                    </div>
                  ) : (
                    <Button variant="soft" className="w-full">
                      <Play size={18} />
                      Begin Challenge
                    </Button>
                  )}
                </div>
              </Card>
              )}
            </motion.section>
          )}

          {/* 4. Teen Zone - Entry Card with vibrant rose/pink theme */}
          <motion.section variants={cardVariants} className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-1 h-5 bg-gradient-to-b from-rose-400 to-pink-500 rounded-full" />
              <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A7F7A]">
                Teen Zone
              </h2>
            </div>
            
            <Card 
              className="overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-fuchsia-500 shadow-lg"
              interactive
              onClick={() => navigate("/teen")}
              data-testid="teen-zone-card"
            >
              <div className="p-6 flex items-center gap-5">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
                    <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
                    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/>
                    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-xl text-white mb-1">
                    Teen Zone
                  </h3>
                  <p className="text-white/90 text-sm">
                    21-day challenges, affirmations & quick resets
                  </p>
                </div>
                <ChevronRight size={24} className="text-white/70" />
              </div>
            </Card>
          </motion.section>

          {/* 5. Parent Zone - Entry Card with vibrant teal theme */}
          <motion.section variants={cardVariants} className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-1 h-5 bg-gradient-to-b from-teal-400 to-emerald-500 rounded-full" />
              <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A7F7A]">
                Parent Zone
              </h2>
            </div>
            
            <Card 
              className="overflow-hidden bg-gradient-to-r from-teal-500 via-emerald-500 to-green-500 shadow-lg"
              interactive
              onClick={() => navigate("/parent")}
              data-testid="parent-area-card"
            >
              <div className="p-6 flex items-center gap-5">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-xl text-white mb-1">
                    Parent Zone
                  </h3>
                  <p className="text-white/90 text-sm">
                    Track your teen's progress & family challenges
                  </p>
                </div>
                <ChevronRight size={24} className="text-white/70" />
              </div>
            </Card>
          </motion.section>

          {/* 6. The Edge — matches /networker dark-amber premium aesthetic */}
          <motion.section variants={cardVariants} className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-1 h-5 rounded-full"
                   style={{ background: "linear-gradient(to bottom, rgba(217,165,90,0.85), rgba(180,120,60,0.85))" }} />
              <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A7F7A]">
                The Edge
              </h2>
            </div>
            
            <Card 
              className="relative overflow-hidden shadow-lg"
              interactive
              onClick={() => navigate("/networker")}
              data-testid="networker-zone-card"
              style={{
                background: "linear-gradient(135deg, #1A1410 0%, #0E0B08 60%, #050402 100%)",
                border: "1px solid rgba(217,165,90,0.30)",
                boxShadow: "0 22px 48px -24px rgba(217,165,90,0.55), inset 0 1px 0 rgba(255,255,255,0.04)",
              }}
            >
              {/* Slow-drifting amber glow */}
              <div aria-hidden className="pointer-events-none absolute inset-0 opacity-90"
                   style={{ background: "radial-gradient(circle at 78% 32%, rgba(217,165,90,0.18) 0%, transparent 55%)" }} />
              {/* Hairline top gradient */}
              <div aria-hidden className="pointer-events-none absolute top-0 inset-x-0 h-px"
                   style={{ background: "linear-gradient(90deg, transparent, rgba(217,165,90,0.55), transparent)" }} />
              <div className="relative p-6 flex items-center gap-5">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                     style={{
                       background: "linear-gradient(135deg, rgba(217,165,90,0.32), rgba(217,165,90,0.08))",
                       boxShadow: "0 10px 30px -12px rgba(217,165,90,0.65), inset 0 1px 0 rgba(255,255,255,0.08)",
                     }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="rgba(255,225,180,0.95)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="20" x2="12" y2="10"/>
                    <line x1="18" y1="20" x2="18" y2="4"/>
                    <line x1="6" y1="20" x2="6" y2="16"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-[0.4em] text-amber-500/85 mb-1.5 font-medium">
                    THE EDGE
                  </p>
                  <h3 className="font-semibold text-xl text-white mb-0.5" style={{ letterSpacing: "-0.012em" }}>
                    High Performance Layer
                  </h3>
                  <p className="text-white/55 text-sm italic"
                     style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "14px" }}>
                    Use this daily. Keep it simple.
                  </p>
                </div>
                <ChevronRight size={22} className="text-amber-300/75" />
              </div>
            </Card>
          </motion.section>

          {/* Bank of Reclaim - Abundance Visualization Tool */}
          <motion.section variants={cardVariants} className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-1 h-5 bg-gradient-to-b from-slate-400 to-slate-600 rounded-full" />
              <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A7F7A]">
                Abundance Tools
              </h2>
            </div>
            {access && !access.abundance?.unlocked ? (
              <LockedCard area="abundance">
                <Card
                  className="overflow-hidden bg-gradient-to-br from-slate-50 to-white border border-slate-200 shadow-sm"
                  data-testid="bank-of-reclaim-card"
                >
                  <div className="p-6 flex items-center gap-5">
                    <div className="w-16 h-16 bg-gradient-to-br from-slate-700 to-slate-900 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="8" width="18" height="12" rx="2"/>
                        <path d="M3 10h18"/>
                        <path d="M7 15h.01"/>
                        <path d="M7 12h4"/>
                        <path d="M17 15h-4"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-slate-800 mb-1">
                        Bank of Reclaim
                      </h3>
                      <p className="text-slate-500 text-sm">
                        Visualise your abundant reality
                      </p>
                    </div>
                  </div>
                </Card>
              </LockedCard>
            ) : (
            <Card 
              className="overflow-hidden bg-gradient-to-br from-slate-50 to-white border border-slate-200 shadow-sm"
              interactive
              onClick={() => navigate("/bank-of-reclaim")}
              data-testid="bank-of-reclaim-card"
            >
              <div className="p-6 flex items-center gap-5">
                <div className="w-16 h-16 bg-gradient-to-br from-slate-700 to-slate-900 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="8" width="18" height="12" rx="2"/>
                    <path d="M3 10h18"/>
                    <path d="M7 15h.01"/>
                    <path d="M7 12h4"/>
                    <path d="M17 15h-4"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-slate-800 mb-1">
                    Bank of Reclaim
                  </h3>
                  <p className="text-slate-500 text-sm">
                    Visualise your abundant reality
                  </p>
                </div>
                <ChevronRight size={24} className="text-slate-400" />
              </div>
            </Card>
            )}
          </motion.section>

          {/* 7. Continue Your Journey */}
          {data?.continue_lesson && (
            <motion.section variants={cardVariants} className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-1 h-5 bg-[#2D4A2A] rounded-full" />
                <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A7F7A]">
                  Continue Your Journey
                </h2>
              </div>
              
              <Card 
                className="p-6" 
                interactive 
                onClick={() => navigate(`/lesson/${data.continue_lesson.id}?program=${data.continue_program?.id}&module=${data.continue_lesson.module_id}`)}
                data-testid="continue-card"
              >
                <div className="flex items-center gap-5">
                  <div className="w-14 h-14 bg-[#2D4A2A] rounded-2xl flex items-center justify-center shadow-md">
                    <Play className="text-white ml-0.5" size={22} />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs uppercase tracking-wider text-[#7A7F7A] font-medium mb-1">
                      {data.continue_program?.title}
                    </p>
                    <h3 className="font-heading text-lg text-[#1A1D1A]">
                      {data.continue_lesson.title}
                    </h3>
                    <p className="text-sm text-[#7A7F7A] mt-1 flex items-center gap-1">
                      <Clock size={14} /> {data.continue_lesson.estimated_minutes} min
                    </p>
                  </div>
                  <ChevronRight size={22} className="text-[#9A9F9A]" />
                </div>
              </Card>
            </motion.section>
          )}

          {/* Teen Zone Demo Section */}
          <motion.section variants={cardVariants} className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-1 h-5 bg-gradient-to-b from-violet-500 to-purple-500 rounded-full" />
              <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A7F7A]">
                Teen Experience
              </h2>
            </div>
            
            <Card 
              className="overflow-hidden bg-gradient-to-br from-[#0f0f1a] to-[#1a1a2e] border-0"
              data-testid="teen-zone-demo-card"
            >
              <div className="p-6 relative overflow-hidden">
                {/* Animated background orbs */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-violet-500/20 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-pink-500/20 rounded-full blur-2xl" />
                
                <div className="relative z-10">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-violet-500/30">
                      <Sparkles size={26} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-semibold text-lg">Personalized Teen Zone</p>
                      <p className="text-white/60 text-sm mt-0.5">Adapts to age & preferences</p>
                    </div>
                  </div>
                  
                  <p className="text-white/70 text-sm mb-5">
                    A unique experience that transforms based on age group. Try all three themes: 
                    <span className="text-violet-400"> Calm Cloud</span>, 
                    <span className="text-cyan-400"> Digital Horizon</span>, and 
                    <span className="text-purple-300"> Midnight Zen</span>.
                  </p>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={() => navigate("/teen/zone")}
                      className="flex-1 py-3 px-4 bg-gradient-to-r from-violet-500 to-purple-600 rounded-xl text-white font-medium text-sm hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                      data-testid="enter-teen-zone-btn"
                    >
                      Enter Teen Zone
                      <ChevronRight size={16} />
                    </button>
                    <button
                      onClick={() => {
                        localStorage.removeItem('reclaim_teen_preferences');
                        navigate("/teen/zone");
                      }}
                      className="py-3 px-4 bg-white/10 hover:bg-white/20 rounded-xl text-white/80 font-medium text-sm transition-colors"
                      data-testid="reset-teen-demo-btn"
                    >
                      Reset & Demo
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          </motion.section>

          {/* Bottom spacing */}
          <div className="h-6" />
        </div>
      </motion.div>
      
      {/* Streak Milestone Celebration */}
      {showMilestone && milestoneStreak && (
        <StreakMilestoneModal 
          streakCount={milestoneStreak}
          onClose={() => setShowMilestone(false)}
        />
      )}

      {/* Spinal Healing milestone — a wordless golden ceremony fired
          once per lifetime milestone (7/14/30/50/100-day streaks,
          first session, family-first). */}
      {spinalMilestone && (
        <SpinalMilestoneBloom
          milestone={spinalMilestone}
          onClose={() => setSpinalMilestone(null)}
        />
      )}
      
      {/* Alignment Check Modal */}
      <AlignmentCheck 
        isOpen={showAlignmentCheck}
        onClose={() => setShowAlignmentCheck(false)}
        onComplete={(alignmentResult) => {
          // Refresh alignment data after completion
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/alignment/today`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` }
          }).then(r => r.json()).then(data => setAlignmentData(data)).catch(() => {});
        }}
      />
    </PageWrapper>
  );
};
